import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Stack, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { ClosedBusinessesAPI } from "../../api/closed-business";
import { filterArrayByString, formatCurrency } from "../helper";
import VisibilityIcon from "../../assets/SVGs/Login/VisibilityIcon";
import EditClosedBusinessModal from "../Modal/EditClosedBusinessModal";
import { defaultEditFormState } from "../constant";
import MemberNameCell from "../MemberNameCell";

const isRecurringRevenueActive = (row) => {
  if (!row?.recurrent) return false;
  if (!row?.last_day_of_payment) return true;
  const lastDay = dayjs(row.last_day_of_payment);
  if (!lastDay.isValid()) return true;
  return !lastDay.isBefore(dayjs(), "day");
};

const isSenderInactive = (row) => row?.closed_business_sender?.status === false;

const RecurringClosedBusinessGrid = ({
  isLoading,
  setIsLoading,
  searchText,
}) => {
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [data, setData] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(defaultEditFormState);

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await ClosedBusinessesAPI.getRecurringClosedBusinesses());
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/login");
      }
      console.error("Error fetching recurring closed businesses", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const columns = [
    {
      field: "closed_business_name",
      headerName: "Closed Business Name",
      flex: 1,
      minWidth: isMobileTable ? 220 : undefined,
      renderCell: (param) => param?.row?.closed_business_name,
    },
    {
      field: "closed_business_sender",
      headerName: "Closed Business Sender",
      flex: 1,
      minWidth: isMobileTable ? 230 : undefined,
      valueGetter: (params) => `${params?.first_name} ${params?.last_name}`,
      renderCell: (param) => (
        <MemberNameCell member={param?.row?.closed_business_sender} />
      ),
    },
    {
      field: "sender_active",
      headerName: "Sender Active",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      valueGetter: (_value, row) =>
        row?.closed_business_sender?.status ? "Yes" : "No",
      renderCell: (params) =>
        params?.row?.closed_business_sender?.status ? "Yes" : "No",
    },
    {
      field: "closed_business_receiver",
      headerName: "Closed Business Receiver",
      flex: 1,
      minWidth: isMobileTable ? 250 : undefined,
      valueGetter: (params) => `${params?.first_name} ${params?.last_name}`,
      renderCell: (param) => (
        <MemberNameCell member={param?.row?.closed_business_receiver} />
      ),
    },
    {
      field: "closed_business_date",
      headerName: "Closed Business Date",
      flex: 1,
      minWidth: isMobileTable ? 220 : undefined,
      type: "date",
      valueGetter: (params) => dayjs(params).toDate(),
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM/DD/YYYY") : "",
    },
    {
      field: "period",
      headerName: "Period",
      flex: 1,
      minWidth: isMobileTable ? 150 : undefined,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "dollar_amount_per_period",
      headerName: "Amount Per Period",
      flex: 1,
      minWidth: isMobileTable ? 220 : undefined,
      renderCell: (params) =>
        params.value !== null && params.value !== undefined
          ? formatCurrency(params.value)
          : "N/A",
    },
    {
      field: "first_day_of_payment",
      headerName: "First Payment",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      type: "date",
      valueGetter: (params) => (params ? dayjs(params).toDate() : null),
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM/DD/YYYY") : "N/A",
    },
    {
      field: "last_day_of_payment",
      headerName: "Last Payment",
      flex: 1,
      minWidth: isMobileTable ? 180 : undefined,
      type: "date",
      valueGetter: (params) => (params ? dayjs(params).toDate() : null),
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM/DD/YYYY") : "Ongoing",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() =>
            setOpenEditModal({
              open: true,
              id: params?.row?.id,
              title: params?.row?.closed_business_name,
              invitationType: params?.row?.invitation_type,
            })
          }
        >
          <VisibilityIcon sx={{ color: "#64748B" }} />
        </IconButton>
      ),
    },
  ];

  const getRowsData = () => {
    const rows = [...(data?.items || [])];
    return filterArrayByString(rows, searchText);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        "& .MuiDataGrid-root": {
          borderRadius: "8px",
        },
        "& .recurring-orphan-row": {
          backgroundColor: "#FEE2E2",
          "&:hover": {
            backgroundColor: "#FECACA",
          },
        },
      }}
      className="customDataGridFigma"
    >
      <DataGrid
        rows={getRowsData() || []}
        columns={columns}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 15, 20, 25]}
        checkboxSelection={false}
        rowSelection={false}
        rowHeight={48}
        columnHeaderHeight={40}
        getRowClassName={(params) => {
          const stripe =
            params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
          const orphan =
            isRecurringRevenueActive(params.row) && isSenderInactive(params.row)
              ? "recurring-orphan-row"
              : "";
          return `${stripe} ${orphan}`.trim();
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        localeText={{
          noRowsLabel: "No Recurring Closed Businesses found.",
        }}
        disableVirtualization
      />
      {openEditModal?.open && (
        <EditClosedBusinessModal
          open={openEditModal?.open}
          id={openEditModal?.id}
          handleClose={() => setOpenEditModal(defaultEditFormState)}
          setIsLoading={setIsLoading}
          title={openEditModal?.title}
          invitationType={openEditModal?.invitationType}
        />
      )}
    </Stack>
  );
};

export default RecurringClosedBusinessGrid;
